import { Flex } from '@chakra-ui/react'
import { ThumbsDown, ThumbsUp } from 'lucide-react'
import React, { useEffect, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Close } from '~/components/compositional/QuickActions'
import {
  MangoButton,
  MangoButtonVariant,
} from '~/components/shared/MangoButton'
import { bottomLeftToaster } from '~/components/ui/toaster'
import useCloudwatchLogger from '~/hooks/useCloudwatchLogger'
import { PAGE_HEADING_ID } from '~/hooks/useRouteFocusAndTitle'
import { Size } from '~/types/ThemeTypes'

import { useFeedbackToastStyle } from './componentStyles/FeedbackToast.style'

export const FEEDBACK_TOAST_ID = 'feedback-toast'
export const FEEDBACK_TOAST_DURATION_MS = 15000
const THANKS_DISPLAY_MS = 2000

interface FeedbackToastProps {
  onClose: () => void
  onOpenFeedbackModal: () => void
  /** Called when focus or hover enters the toast, so its timer can stop. */
  onPause?: () => void
  /** Called once neither focus nor hover remains on the toast. */
  onResume?: () => void
}

export const FeedbackToast = (props: FeedbackToastProps) => {
  const { t } = useTranslation()
  const styles = useFeedbackToastStyle()
  const [ratedPositive, setRatedPositive] = useState(false)
  const { logger } = useCloudwatchLogger('FeedbackToast')
  const containerRef = useRef<HTMLDivElement>(null)
  const isFocusedRef = useRef(false)
  const isHoveredRef = useRef(false)
  const focusBeforeToastRef = useRef<HTMLElement | null>(null)
  const restoreFocusOnCloseRef = useRef(false)

  const updatePaused = (next: { focused?: boolean; hovered?: boolean }) => {
    const wasPaused = isFocusedRef.current || isHoveredRef.current
    isFocusedRef.current = next.focused ?? isFocusedRef.current
    isHoveredRef.current = next.hovered ?? isHoveredRef.current
    const isPaused = isFocusedRef.current || isHoveredRef.current
    if (isPaused && !wasPaused) {
      props.onPause?.()
    } else if (!isPaused && wasPaused) {
      props.onResume?.()
    }
  }

  const handleFocus = (event: React.FocusEvent<HTMLDivElement>) => {
    if (!isFocusedRef.current) {
      const cameFrom = event.relatedTarget
      focusBeforeToastRef.current =
        cameFrom instanceof HTMLElement &&
        !event.currentTarget.contains(cameFrom)
          ? cameFrom
          : null
    }
    updatePaused({ focused: true })
  }

  const toastHasFocus = () =>
    !!containerRef.current?.contains(document.activeElement)

  // The toast is removed from the DOM on close, which would otherwise drop
  // keyboard focus to <body>.
  const closeAndRestoreFocus = (shouldRestoreFocus: boolean) => {
    props.onClose()
    if (!shouldRestoreFocus) {
      return
    }
    const previous = focusBeforeToastRef.current
    const target = previous?.isConnected
      ? previous
      : document.getElementById(PAGE_HEADING_ID)
    target?.focus({ preventScroll: true })
  }

  const handleBlur = (event: React.FocusEvent<HTMLDivElement>) => {
    if (!event.currentTarget.contains(event.relatedTarget as Node | null)) {
      updatePaused({ focused: false })
    }
  }

  useEffect(() => {
    if (!ratedPositive) {
      return
    }
    // The thumbs-up button is gone once rated, so keep focus in the toast.
    if (restoreFocusOnCloseRef.current) {
      containerRef.current?.querySelector<HTMLButtonElement>('button')?.focus()
    }
    const timer = setTimeout(() => {
      closeAndRestoreFocus(restoreFocusOnCloseRef.current || toastHasFocus())
    }, THANKS_DISPLAY_MS)
    return () => clearTimeout(timer)
  }, [ratedPositive])

  const handleLike = () => {
    logger?.info('call_fb_thumbs_up')
    restoreFocusOnCloseRef.current = toastHasFocus()
    setRatedPositive(true)
  }

  const handleDislike = () => {
    logger?.info('call_fb_thumbs_down')
    props.onOpenFeedbackModal()
    props.onClose()
  }

  const handleClose = () => {
    logger?.info('call_fb_dismiss')
    closeAndRestoreFocus(toastHasFocus())
  }

  return (
    <Flex
      ref={containerRef}
      css={styles.container}
      onFocus={handleFocus}
      onBlur={handleBlur}
      onMouseEnter={() => updatePaused({ hovered: true })}
      onMouseLeave={() => updatePaused({ hovered: false })}
    >
      <Flex justifyContent="space-between">
        <Flex css={styles.header}>
          {t('webPhone.feedback.toast.header', 'Rate your call audio')}
        </Flex>
        <Close
          size={Size.sm}
          iconProps={{ boxSize: '0.625rem' }}
          onClick={handleClose}
          tooltipText={t('webPhone.feedback.toast.dismiss', 'Dismiss')}
        />
      </Flex>
      {ratedPositive ? (
        <Flex css={styles.message}>
          {t('webPhone.feedback.toast.thanks', 'Thanks!')}
        </Flex>
      ) : (
        <Flex gap="5" justifyContent="center">
          <MangoButton
            variant={MangoButtonVariant.Default_200}
            width={4}
            onClick={handleLike}
            aria-label={t(
              'webPhone.feedback.toast.goodQuality',
              'Good call quality',
            )}
          >
            <ThumbsUp aria-hidden="true" />
          </MangoButton>
          <MangoButton
            variant={MangoButtonVariant.Default_200}
            width={4}
            onClick={handleDislike}
            aria-label={t(
              'webPhone.feedback.toast.poorQuality',
              'Poor call quality',
            )}
          >
            <ThumbsDown aria-hidden="true" />
          </MangoButton>
        </Flex>
      )}
    </Flex>
  )
}

/**
 * Shows the post-call feedback toast. Its auto-dismiss timer is paused while
 * the toast has keyboard focus or mouse hover.
 */
export const showFeedbackToast = (onOpenFeedbackModal: () => void) => {
  const dismiss = () => bottomLeftToaster.dismiss(FEEDBACK_TOAST_ID)
  bottomLeftToaster.create({
    id: FEEDBACK_TOAST_ID,
    duration: FEEDBACK_TOAST_DURATION_MS,
    meta: {
      closable: true,
      customRender: () => (
        <FeedbackToast
          onClose={dismiss}
          onOpenFeedbackModal={onOpenFeedbackModal}
          onPause={() => bottomLeftToaster.pause(FEEDBACK_TOAST_ID)}
          onResume={() => bottomLeftToaster.resume(FEEDBACK_TOAST_ID)}
        />
      ),
    },
  })
}
